import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Select, MenuItem, FormControl } from '@mui/material';
import ModalGeneric from './ModalGeneric';
import { PUT } from '../Generic/CrudRequest';
import EncryptionUtils from '../../Utils/EncryptionUtils';
import Alerts from '../../Utils/Alerts';

export default function LanguageButton({ config, onLanguageChange }) {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  
  // Función para manejar el cambio de idioma en el select
  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    const currentLang = i18n.language;
    
    // Si el idioma seleccionado es diferente al actual, mostrar modal
    if (newLang !== currentLang) {
      setSelectedLanguage(newLang);
      setShowModal(true);
    }
  };

  // Función para confirmar el cambio de idioma
  const confirmLanguageChange = async () => {
    setIsUpdating(true);
    try {
      // Obtener datos del usuario actual
      const userData = JSON.parse(EncryptionUtils.getDecryptedData("user"));
      
      // Mapear idioma a ID (1 = es, 2 = en)
      const languageId = selectedLanguage === 'es' ? 1 : 2;
      
      // Actualizar idioma en la base de datos
      const response = await PUT(`${config.url}/api/User/updateLanguage`, {
        userId: userData.userId,
        languageId: languageId
      });

      // Verificar la respuesta del API
      // console.log('Respuesta del API updateLanguage:', response);
      
      const apiLanguage = response?.data?.language || selectedLanguage;
      const apiLanguageBase = apiLanguage.split("-")[0]; // Extraer "es" de "es-MX"

      // Actualizar datos del usuario con la respuesta completa de la API
      const updatedUserData = response?.data?.user || userData;
      
      // Si la API devuelve datos completos, usarlos; si no, actualizar solo el idioma
      if (response?.data?.user) {
        // Usar los datos completos devueltos por la API
        EncryptionUtils.storeEncryptedData("user", JSON.stringify(updatedUserData));
        
        // Actualizar permisos en localStorage (igual que en el login)
        if (updatedUserData.user?.permisosWebUsuarios) {
          localStorage.setItem(
            "permissions",
            JSON.stringify(updatedUserData.user.permisosWebUsuarios)
          );
        }
      } else {
        // Fallback: actualizar solo el idioma localmente
        userData.idLanguage = languageId;
        userData.idLanguageNavigation = {
          idLanguage: languageId,
          descripcion: apiLanguageBase === 'es' ? 'Español' : 'English',
          claveIdioma: apiLanguage
        };
        EncryptionUtils.storeEncryptedData("user", JSON.stringify(userData));
      }
      
      // Cambiar idioma en la aplicación usando el idioma de la API
      await i18n.changeLanguage(apiLanguageBase);
      
      // Actualizar en localStorage
      localStorage.setItem("language", apiLanguageBase);
      
      // Forzar re-renderización de todos los componentes que usan i18n
      // Esto incluye el drawer automáticamente
      i18n.emit('languageChanged', apiLanguageBase);
      
      // Notificar al componente padre para recargar permisos
      if (onLanguageChange) {
        onLanguageChange(updatedUserData);
      }

      setShowModal(false);
      setSelectedLanguage('');
      
      // Mostrar mensaje de éxito
      setAlertMessage(t("users.languageButton.updateSuccess"));
      setAlertType("success");
      setShowAlert(true);

      // Recargar la página inmediatamente
      //window.location.reload();
    } catch (error) {
      console.error('Error al actualizar idioma:', error);
      
      // Mostrar mensaje de error
      setAlertMessage(t("users.languageButton.updateError"));
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setIsUpdating(false);
    }
  };

  // Función para cancelar el cambio
  const cancelLanguageChange = () => {
    setShowModal(false);
    setSelectedLanguage('');
  };

  // Obtener el idioma actual
  const currentLanguage = i18n.language;

  const handleCloseAlert = () => {
    setShowAlert(false);
  };
  
  return (
    <Box>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={handleCloseAlert}
        />
      )}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select
          value={currentLanguage}
          onChange={handleLanguageChange}
          disabled={isUpdating}
          sx={{
            color: '#fff',
            backgroundColor: 'rgb(4, 49, 99)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(32, 31, 31, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiSvgIcon-root': {
              color: '#fff',
            },
          }}
        >
          <MenuItem value="es">{t("users.languageButton.spanish")}</MenuItem>
          <MenuItem value="en">{t("users.languageButton.english")}</MenuItem>
        </Select>
      </FormControl>

      {/* Modal de confirmación */}
      <ModalGeneric
        open={showModal}
        title={t("users.languageButton.changeTitle")}
        description={t("users.languageButton.changeDescription", { 
          current: currentLanguage === 'es' ? t("users.languageButton.spanish") : t("users.languageButton.english"),
          new: selectedLanguage === 'es' ? t("users.languageButton.spanish") : t("users.languageButton.english")
        })}
        onClose={cancelLanguageChange}
        onAccept={confirmLanguageChange}
      />
    </Box>
  );
}